import { useEffect, useState } from "react";

import {
  FilesystemAPI,
  type AutocategorizeRunRecord
} from "../../caseFiles/api/filesystemApi";

const POLL_MS = 3000;

/** Statuses the run can still leave on its own; anything else stops the poll. */
const IN_FLIGHT = new Set<AutocategorizeRunRecord["status"]>([
  "running",
  "applying"
]);

/**
 * Follow an AI-organizer run the agent started until it has a plan, failed or
 * was applied. The agent stops after `start_autocategorize`, and the run is not
 * in the workspace tree, so without this the card would sit on the `running`
 * status baked into the tool output forever.
 */
export const useAutocategorizeRun = (
  individualId: string,
  runId: string | null
): AutocategorizeRunRecord | null => {
  const [record, setRecord] = useState<AutocategorizeRunRecord | null>(null);

  useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const { run } = await FilesystemAPI.getAutocategorizeRun(
          individualId,
          runId
        );
        if (cancelled) return;
        setRecord(run);
        if (IN_FLIGHT.has(run.status)) timer = setTimeout(tick, POLL_MS);
      } catch {
        // Stop following; the card falls back to the status in the tool output.
      }
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [individualId, runId]);

  return record;
};
